// Native WebSocket wrapper — factory pattern (v0.2.0)
// Apps resuelven env + pasan url como arg. Zero cast cross-framework.

export type WSState = 'connecting' | 'open' | 'closed' | 'reconnecting'
export type WSEvent<T = unknown> = { channel: string; data: T }

export type WSClient = {
  connect(authToken: string): Promise<void>
  disconnect(): void
  subscribe<T>(channel: string, handler: (data: T) => void): () => void
  send<T>(channel: string, payload: T): void
  onStateChange(cb: (state: WSState) => void): () => void
  getState(): WSState
}

export type WSClientConfig = {
  /** WebSocket URL (ej. "wss://api-crm.ingenios.com.ar/ws") */
  url: string
  /** Reconnect strategy. Default: exponential backoff 1s → 30s max */
  reconnect?: {
    initialDelay?: number
    maxDelay?: number
  }
  /** Heartbeat interval ms (ping msg). Default: 30000 */
  heartbeat?: number
}

/**
 * Create WSClient instance con config injected por la app.
 *
 * @example (Vite app)
 * ```ts
 * export const wsClient = createWSClient({ url: import.meta.env.VITE_WS_URL })
 * ```
 *
 * @example (Next.js app)
 * ```ts
 * export const wsClient = createWSClient({ url: process.env.NEXT_PUBLIC_WS_URL ?? '' })
 * ```
 */
export function createWSClient(config: WSClientConfig): WSClient {
  const reconnectConfig = {
    initialDelay: config.reconnect?.initialDelay ?? 1000,
    maxDelay: config.reconnect?.maxDelay ?? 30_000,
  }
  const heartbeatMs = config.heartbeat ?? 30_000

  let ws: WebSocket | null = null
  let state: WSState = 'closed'
  let reconnectAttempts = 0
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  let heartbeatInterval: ReturnType<typeof setInterval> | null = null
  const subscriptions = new Map<string, Set<(data: unknown) => void>>()
  const stateListeners = new Set<(state: WSState) => void>()
  let authToken = ''

  function setState(newState: WSState) {
    state = newState
    stateListeners.forEach((listener) => listener(state))
  }

  function connect(token: string): Promise<void> {
    authToken = token
    return new Promise((resolve, reject) => {
      setState('connecting')
      const url = `${config.url}?token=${encodeURIComponent(authToken)}`
      ws = new WebSocket(url)

      ws.onopen = () => {
        reconnectAttempts = 0
        setState('open')
        heartbeatInterval = setInterval(() => {
          ws?.send(JSON.stringify({ type: 'ping' }))
        }, heartbeatMs)
        resolve()
      }

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data) as WSEvent
          const handlers = subscriptions.get(msg.channel)
          handlers?.forEach((handler) => handler(msg.data))
        } catch (err) {
          console.error('[ws] parse error', err)
        }
      }

      ws.onclose = () => {
        if (heartbeatInterval) clearInterval(heartbeatInterval)
        setState('closed')
        scheduleReconnect()
      }

      ws.onerror = (err) => {
        console.error('[ws] error', err)
        reject(err)
      }
    })
  }

  function scheduleReconnect() {
    if (reconnectTimeout) clearTimeout(reconnectTimeout)
    const delay = Math.min(
      reconnectConfig.initialDelay * Math.pow(2, reconnectAttempts),
      reconnectConfig.maxDelay,
    )
    reconnectAttempts++
    setState('reconnecting')
    reconnectTimeout = setTimeout(() => {
      if (authToken) connect(authToken).catch(() => scheduleReconnect())
    }, delay)
  }

  function disconnect() {
    if (reconnectTimeout) clearTimeout(reconnectTimeout)
    if (heartbeatInterval) clearInterval(heartbeatInterval)
    authToken = ''
    ws?.close()
    ws = null
    setState('closed')
  }

  function subscribe<T>(channel: string, handler: (data: T) => void): () => void {
    if (!subscriptions.has(channel)) subscriptions.set(channel, new Set())
    subscriptions.get(channel)!.add(handler as (data: unknown) => void)
    return () => {
      subscriptions.get(channel)?.delete(handler as (data: unknown) => void)
    }
  }

  function send<T>(channel: string, payload: T): void {
    if (state !== 'open') {
      console.warn('[ws] send ignored — not connected')
      return
    }
    ws?.send(JSON.stringify({ channel, data: payload }))
  }

  function onStateChange(cb: (state: WSState) => void): () => void {
    stateListeners.add(cb)
    return () => stateListeners.delete(cb) as unknown as void
  }

  return { connect, disconnect, subscribe, send, onStateChange, getState: () => state }
}
